import React, { useMemo, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { Arbeitsschritt } from './types';
import { Contact } from '../../types';
import type { ProjektMerkmale } from '../planungsphase/types';
import { MERKMAL_LABEL } from '../planungsphase/fragenkatalog';
import { MUSTER_ZEILEN } from './musterVorlage';
import {
  BedingterDokumentVorschlag, PlanZeitraum, baueMusterZeilen, bedingteAlsAuftraege, bedingteDokumenteNachMerkmalen,
  gewerkVorschlaege, gruppiereMusterZeilen, normBezeichnung,
} from './uebernahme';
import {
  mitWahl, musterVorauswahl, wirksameAuswahl, zaehleVorauswahl, type MusterVorauswahlEintrag,
} from './musterVorauswahl';
import type { PlanungStand } from './planungAnbindung';
import { fehlerMeldung, schreibeErgaenzungen, type SchrittDb } from './unterlagenErgaenzen';
import { BedingteDokumente } from './BedingteDokumente';
import { Button } from '../Button';
import { CheckSquare, ChevronDown, ChevronRight, Loader2, Square } from 'lucide-react';

// Schritt 3 des Anlage-Assistenten: Abschnitte und Zeilen der Muster-Vorlage
// einzeln wählen. Ersetzt das Alles-oder-nichts des Muster-Imports; bereits
// vorhandene Zeilen sind gesperrt, damit nichts doppelt entsteht.
//
// Vorauswahl nach den Merkmalen der Planungsphase (Entscheid E6, siehe
// `musterVorauswahl.ts`): unbedingte Zeilen und bejahte Merkmale vorgewählt,
// ungeklärte vorgewählt und gelb markiert, nur ein ausdrückliches Nein wählt
// ab — sichtbar, nicht versteckt. Ohne Planungsphase alles an wie bisher.
// Gehalten wird jede AUSDRÜCKLICHE Wahl des Nutzers (auch wenn sie der
// Vorauswahl entspricht); alles andere folgt der Vorauswahl. Trifft die
// Planungsphase erst nach dem Öffnen ein, ändert sie nur Zeilen, die der
// Nutzer nicht angefasst hat.

interface Props {
  sigeProjektId: string;
  zeitraum: PlanZeitraum;
  schritte: Arbeitsschritt[];
  beteiligte: Contact[];
  /** Merkmale der Planungsphase; `null` ohne Planungsphase oder vor dem Laden. */
  merkmale: ProjektMerkmale | null;
  planungStand: PlanungStand;
  onUebernommen: () => void | Promise<void>;
  melde: (text: string, ton?: 'info' | 'fehler') => void;
}

/** Datenbankzugriff der Dokument-Übernahme — über die Schreib-Warteschlange je Schritt. */
const WIZARD_DB: SchrittDb = {
  lese: async (id) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
  schreibe: async (id, felder) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').update(felder).eq('id', id).select().single();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
};

const merkmalText = (merkmale: readonly string[]): string =>
  merkmale.map(k => MERKMAL_LABEL[k] || k).join(' · ');

/** Kennzeichen einer Zeile nach ihrem Vorauswahl-Stand — unbedingte Zeilen tragen keins. */
const StandMarke: React.FC<{ eintrag: MusterVorauswahlEintrag }> = ({ eintrag }) => {
  if (eintrag.stand === 'unbedingt') return null;
  const stil = eintrag.stand === 'ungeklaert'
    ? 'bg-amber-50 text-amber-800 border-amber-300'
    : eintrag.stand === 'verneint'
      ? 'bg-slate-100 text-slate-600 border-slate-300'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const text = eintrag.stand === 'ungeklaert' ? 'Merkmal ungeklärt' : eintrag.stand === 'verneint' ? 'Merkmal verneint' : 'Merkmal bejaht';
  return (
    <span className="inline-flex items-center gap-1 ml-2 align-middle" title={merkmalText(eintrag.merkmale)}>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${stil}`}>{text}</span>
      <span className="text-[11px] text-slate-500 truncate max-w-[22rem]">{merkmalText(eintrag.merkmale)}</span>
    </span>
  );
};

export const WizardStepAbschnitte: React.FC<Props> = ({
  sigeProjektId, zeitraum, schritte, beteiligte, merkmale, planungStand, onUebernommen, melde,
}) => {
  const gruppen = useMemo(() => gruppiereMusterZeilen(MUSTER_ZEILEN), []);

  // Bereits im Plan stehende Bezeichnungen — diese Zeilen bleiben gesperrt.
  const belegt = useMemo(
    () => new Set(schritte.map(s => normBezeichnung(s.bezeichnung)).filter(Boolean)),
    [schritte],
  );
  const istBelegt = (i: number) => belegt.has(normBezeichnung(MUSTER_ZEILEN[i].bezeichnung));

  // Vorauswahl aus den Merkmalen; daneben jede ausdrückliche Wahl des Nutzers.
  const vorauswahl = useMemo(() => musterVorauswahl(MUSTER_ZEILEN, merkmale), [merkmale]);
  const zaehler = useMemo(() => zaehleVorauswahl(vorauswahl), [vorauswahl]);
  const [wahl, setWahl] = useState<Map<number, boolean>>(() => new Map());
  const auswahl = useMemo(
    () => wirksameAuswahl(vorauswahl, wahl, i => belegt.has(normBezeichnung(MUSTER_ZEILEN[i].bezeichnung))),
    [vorauswahl, wahl, belegt],
  );

  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [standardGewerk, setStandardGewerk] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [dokumenteLaufen, setDokumenteLaufen] = useState(false);

  const gewerke = useMemo(() => gewerkVorschlaege(beteiligte), [beteiligte]);

  // Bedingte Katalogdokumente, deren Bedingung die Planungsphase bejaht —
  // gerechnet über die Zeilen, die schon im Plan stehen.
  const bedingte = useMemo(() => bedingteDokumenteNachMerkmalen(schritte, merkmale), [schritte, merkmale]);

  const vorhandenImAbschnitt = (abschnitt: string) =>
    schritte.filter(s => (s.hauptabschnitt || '') === abschnitt).length;

  const umschaltenZeile = (i: number) => {
    if (istBelegt(i)) return;
    const wert = !auswahl.has(i);
    setWahl(prev => mitWahl(prev, [i], wert));
  };

  const umschaltenGruppe = (indizes: number[]) => {
    const frei = indizes.filter(i => !istBelegt(i));
    const alleAn = frei.every(i => auswahl.has(i));
    setWahl(prev => mitWahl(prev, frei, !alleAn));
  };

  const uebernehmen = async () => {
    setSpeichert(true);
    try {
      const gewaehlt = MUSTER_ZEILEN.filter((_, i) => auswahl.has(i));
      const { rows, duplikate } = baueMusterZeilen(gewaehlt, {
        sigeProjektId,
        zeitraum,
        startSortierung: schritte.length,
        vorhandeneBezeichnungen: schritte.map(s => s.bezeichnung),
        standardGewerk,
      });
      if (rows.length === 0) {
        melde('Nichts übernommen: die Auswahl steht bereits vollständig im Plan.', 'fehler');
        return;
      }
      const { error } = await supabase.from('sige_arbeitsschritte').insert(rows);
      if (error) throw error;
      melde(
        `${rows.length} Zeile(n) aus der Muster-Vorlage übernommen.` +
        (duplikate.length ? ` ${duplikate.length} bereits vorhandene ausgelassen.` : ''),
      );
      await onUebernommen();
    } catch (e: unknown) {
      console.error('Muster-Übernahme fehlgeschlagen', e);
      melde(`Übernahme fehlgeschlagen: ${describeError(e)}`, 'fehler');
    } finally {
      setSpeichert(false);
    }
  };

  const dokumenteUebernehmen = async (liste: readonly BedingterDokumentVorschlag[]) => {
    const auftraege = bedingteAlsAuftraege(liste, schritte);
    if (auftraege.length === 0) {
      melde('Nichts übernommen: die Dokumente stehen bereits an den Arbeitsschritten.', 'fehler');
      return;
    }
    setDokumenteLaufen(true);
    try {
      const r = await schreibeErgaenzungen(auftraege, WIZARD_DB);
      const fehler = fehlerMeldung(r.fehler);
      if (fehler) melde(fehler, 'fehler');
      else melde(`${r.dokumenteGeschrieben} Dokument(e) an ${r.geschrieben.length} Arbeitsschritt(e) gehängt.`);
      await onUebernommen();
    } catch (e: unknown) {
      console.error('Dokument-Übernahme fehlgeschlagen', e);
      melde(`Übernahme fehlgeschlagen: ${describeError(e)}`, 'fehler');
    } finally {
      setDokumenteLaufen(false);
    }
  };

  const anzahlGewaehlt = auswahl.size;
  const mitPlanung = merkmale !== null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Die Muster-Vorlage deckt die zehn Hauptabschnitte des SiGe-Plans ab. Abschnitte lassen sich
        komplett abwählen oder aufklappen und zeilenweise anpassen. Die Zeiträume werden aus der
        typischen Bauphase des Abschnitts errechnet und auf die Bauzeit geklemmt.
      </p>

      {mitPlanung ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 leading-snug">
          <span className="font-bold">Vorauswahl nach der Planungsphase:</span>{' '}
          {zaehler.verneint > 0
            ? `${zaehler.verneint} Zeile(n) abgewählt, weil das zugehörige Merkmal ausdrücklich verneint ist`
            : 'kein Merkmal ausdrücklich verneint, deshalb nichts abgewählt'}
          {zaehler.ungeklaert > 0 && (
            <>; <span className="font-bold">{zaehler.ungeklaert} Zeile(n) mit ungeklärtem Merkmal</span> bleiben
            vorgewählt und sind gelb markiert, denn ungeklärt heißt nicht nein</>
          )}
          . Jede Zeile lässt sich weiter einzeln an- oder abwählen.
        </div>
      ) : planungStand === 'laedt' ? (
        <div className="text-[11px] text-slate-500">Planungsphase wird gelesen; die Vorauswahl folgt ihr, sobald sie vorliegt.</div>
      ) : (
        <div className="text-[11px] text-slate-500">
          Keine Planungsphase angelegt, alle Zeilen sind vorgewählt. Mit Merkmalen aus der Planungsphase
          würde der Assistent verneinte Merkmale abwählen.
        </div>
      )}

      {gewerke.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Gewerk aus dem Projektverteiler übernehmen (optional)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {gewerke.map(g => (
              <button
                key={g}
                onClick={() => setStandardGewerk(standardGewerk === g ? '' : g)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  standardGewerk === g
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            {standardGewerk
              ? `„${standardGewerk}" wird bei Muster-Zeilen ohne eigenes Gewerk eingetragen.`
              : 'Ohne Auswahl bleibt das Gewerk der Muster-Zeile stehen.'}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {gruppen.map(({ abschnitt, indizes }) => {
          const frei = indizes.filter(i => !istBelegt(i));
          const gewaehlt = frei.filter(i => auswahl.has(i)).length;
          const ungeklaert = frei.filter(i => vorauswahl[i].stand === 'ungeklaert').length;
          const verneint = frei.filter(i => vorauswahl[i].stand === 'verneint').length;
          const istOffen = offen.has(abschnitt);
          const vorhanden = vorhandenImAbschnitt(abschnitt);
          return (
            <div key={abschnitt} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="flex items-center gap-2 p-3">
                <button onClick={() => umschaltenGruppe(indizes)} className="shrink-0" title="Abschnitt an-/abwählen" disabled={frei.length === 0}>
                  {gewaehlt > 0 && gewaehlt === frei.length
                    ? <CheckSquare className="w-5 h-5 text-blue-600" />
                    : <Square className={`w-5 h-5 ${gewaehlt > 0 ? 'text-blue-400' : 'text-slate-300'}`} />}
                </button>
                <button
                  onClick={() => setOffen(prev => {
                    const next = new Set(prev);
                    if (next.has(abschnitt)) next.delete(abschnitt); else next.add(abschnitt);
                    return next;
                  })}
                  className="flex-1 text-left flex items-center gap-2 min-w-0"
                >
                  {istOffen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <span className="text-sm font-medium text-slate-800 truncate" title={abschnitt}>{abschnitt}</span>
                </button>
                {ungeklaert > 0 && (
                  <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 shrink-0" title="Zeilen mit ungeklärtem Merkmal: vorgewählt">
                    {ungeklaert} ungeklärt
                  </span>
                )}
                {verneint > 0 && (
                  <span className="text-[11px] text-slate-600 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 shrink-0" title="Zeilen, deren Merkmal verneint ist: abgewählt">
                    {verneint} verneint
                  </span>
                )}
                <span className="text-[11px] text-slate-500 shrink-0">
                  {gewaehlt} / {indizes.length} Musterzeilen
                </span>
                {vorhanden > 0 && (
                  <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0">
                    {vorhanden} im Plan
                  </span>
                )}
              </div>

              {istOffen && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {indizes.map(i => {
                    const zeile = MUSTER_ZEILEN[i];
                    const gesperrt = istBelegt(i);
                    const eintrag = vorauswahl[i];
                    const gelb = !gesperrt && eintrag.stand === 'ungeklaert';
                    return (
                      <div
                        key={i}
                        onClick={() => umschaltenZeile(i)}
                        className={`flex items-start gap-2 px-3 py-2 ${
                          gesperrt ? 'bg-slate-50' : gelb ? 'bg-amber-50/60 cursor-pointer hover:bg-amber-50' : 'cursor-pointer hover:bg-slate-50'
                        }`}
                      >
                        <div className="mt-0.5 shrink-0">
                          {gesperrt
                            ? <CheckSquare className="w-4 h-4 text-slate-300" />
                            : auswahl.has(i)
                              ? <CheckSquare className="w-4 h-4 text-blue-600" />
                              : <Square className="w-4 h-4 text-slate-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm ${gesperrt ? 'text-slate-400' : 'text-slate-700'}`}>
                            {zeile.bezeichnung}
                            {gesperrt && <span className="ml-2 text-[11px] text-slate-400">bereits im Plan</span>}
                            {!gesperrt && mitPlanung && <StandMarke eintrag={eintrag} />}
                          </div>
                          {!!zeile.schutzmassnahmen?.length && (
                            <div className="text-[11px] text-slate-400 truncate">{zeile.schutzmassnahmen.join(' · ')}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BedingteDokumente vorschlaege={bedingte} busy={dokumenteLaufen} onUebernehmen={liste => { void dokumenteUebernehmen(liste); }} />

      <div className="flex justify-end sticky bottom-0 bg-white/90 backdrop-blur-sm py-2">
        <Button variant="primary" onClick={uebernehmen} disabled={speichert || anzahlGewaehlt === 0}>
          {speichert && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Auswahl übernehmen ({anzahlGewaehlt})
        </Button>
      </div>
    </div>
  );
};
